/**
 * Location Autocomplete
 *
 * Geocoding via Photon (Komoot/OSM). Free, no API key, global city coverage.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { reportError } from '@/lib/errors';

// ============================================
// Types
// ============================================

export interface LocationSuggestion {
  /** Dedup key, e.g. "berlin-germany" */
  id: string;
  city: string;
  country: string;
  state?: string;
  displayName: string;
}

// ============================================
// API
// ============================================

const PHOTON_URL = 'https://photon.komoot.io/api/';
const CITY_TYPES = new Set(['city', 'town', 'village', 'borough', 'suburb', 'district', 'municipality']);

function makeId(city: string, country: string): string {
  return `${city}-${country}`.toLowerCase().replace(/\s+/g, '-');
}

export async function fetchLocationSuggestions(
  query: string,
  signal?: AbortSignal,
): Promise<LocationSuggestion[]> {
  try {
    const url = `${PHOTON_URL}?q=${encodeURIComponent(query)}&limit=10&lang=en`;
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`Photon API ${res.status}`);

    const data = await res.json();
    const features: any[] = data?.features ?? [];

    const seen = new Set<string>();
    const results: LocationSuggestion[] = [];

    for (const f of features) {
      const p = f?.properties;
      if (!p?.name || !p?.country) continue;
      if (p.type && !CITY_TYPES.has(p.type)) continue;

      const id = makeId(p.name, p.country);
      if (seen.has(id)) continue;
      seen.add(id);

      results.push({
        id,
        city: p.name,
        country: p.country,
        state: p.state ?? undefined,
        displayName: `${p.name}, ${p.country}`,
      });

      if (results.length >= 8) break;
    }

    return results;
  } catch (err: any) {
    if (err?.name === 'AbortError') return [];
    reportError(err, { context: 'fetchLocationSuggestions', level: 'warning' });
    return [];
  }
}

// ============================================
// React Query Hook
// ============================================

export function useLocationAutocomplete(query: string) {
  return useQuery({
    queryKey: ['locationAutocomplete', query],
    queryFn: ({ signal }) => fetchLocationSuggestions(query, signal),
    enabled: query.length >= 2,
    staleTime: 10 * 60 * 1000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

// ============================================
// Popular Locations (fallback / suggestions)
// ============================================

export const POPULAR_LOCATIONS: LocationSuggestion[] = [
  { id: 'zurich-switzerland', city: 'Zurich', country: 'Switzerland', displayName: 'Zurich, Switzerland' },
  { id: 'geneva-switzerland', city: 'Geneva', country: 'Switzerland', displayName: 'Geneva, Switzerland' },
  { id: 'basel-switzerland', city: 'Basel', country: 'Switzerland', displayName: 'Basel, Switzerland' },
  { id: 'san-francisco-united-states', city: 'San Francisco', country: 'United States', displayName: 'San Francisco, USA' },
  { id: 'new-york-united-states', city: 'New York', country: 'United States', displayName: 'New York, USA' },
  { id: 'london-united-kingdom', city: 'London', country: 'United Kingdom', displayName: 'London, UK' },
  { id: 'berlin-germany', city: 'Berlin', country: 'Germany', displayName: 'Berlin, Germany' },
  { id: 'paris-france', city: 'Paris', country: 'France', displayName: 'Paris, France' },
  { id: 'singapore-singapore', city: 'Singapore', country: 'Singapore', displayName: 'Singapore' },
  { id: 'tel-aviv-israel', city: 'Tel Aviv', country: 'Israel', displayName: 'Tel Aviv, Israel' },
  { id: 'amsterdam-netherlands', city: 'Amsterdam', country: 'Netherlands', displayName: 'Amsterdam, Netherlands' },
  { id: 'stockholm-sweden', city: 'Stockholm', country: 'Sweden', displayName: 'Stockholm, Sweden' },
  { id: 'bangalore-india', city: 'Bangalore', country: 'India', displayName: 'Bangalore, India' },
  { id: 'tokyo-japan', city: 'Tokyo', country: 'Japan', displayName: 'Tokyo, Japan' },
  { id: 'sao-paulo-brazil', city: 'São Paulo', country: 'Brazil', displayName: 'São Paulo, Brazil' },
];
